import { useEffect, useState } from 'react';
import usePrevious from 'Hooks/usePrevious';

export default function useResource(resource = null, args = {}) {
  const {
    allowParentOverride = true,
  } = args;

  // State is being used here to trigger renders in react. The updated attributes can be grabbed off of the resource
  const [attributes, setResourceAttributes] = useState((resource === null) ? {} : resource.attributes()); // eslint-disable-line no-unused-vars

  // Keep track of the resource instance that state is currently based on
  const [stateResource, setStateResource] = useState(resource);
  const prevStateResource = usePrevious(stateResource);

  // Push resource changes from a parent into state
  // This is commonly done by a setCollection in a parent
  // This is not great but it works for now until we invest in saving state on the collection rather than the resource via memoization
  useEffect(() => {
    if (!resource) {
      return;
    }
    const isInitialized = stateResource && prevStateResource;
    const isParentInstanceDifferent = isInitialized && stateResource.instanceId() !== resource.instanceId();

    if (resource && (!isInitialized || (isParentInstanceDifferent && allowParentOverride))) {
      let newResource = resource;
      // Override meta on whatever just came in. It's either something we want to push down or this hook is initializing.
      if (args.metaOverrideSet) {
        newResource = resource.overrideMeta(args.metaOverrideSet);
      }

      // This will only be true during initialization (ie. the first few ms the hook is in use)
      if (!isInitialized && args.initialize) {
        newResource = args.initialize(newResource);
      }
      // Set state
      setStateResource(newResource);
      setResourceAttributes(newResource.attributes());
    }
  }, [resource]);

  function setAttributeInternal(key, value) {
    // Update the attribute. In doing this a new object will be created with attributes inside which will cause
    // the state update below to trigger a re-render.
    const updatedAttributes = stateResource.attr(key, value).attributes();

    // This will stick the exact same object reference for the resource's attributes into state.
    setResourceAttributes(updatedAttributes);
  }

  function setResourceInternal(value) {
    setStateResource(value);
    setResourceAttributes(value.attributes());
  }

  return {
    resource: stateResource,
    setAttribute: setAttributeInternal,
    setResourceDeprecated: setResourceInternal,
    initializeResource: setResourceInternal,
  };
}
